/*
 * My Leads page body (PROD-9977): header, tiles, My shift today, per-level sections, help.
 * Presentational only; MyLeadsPage owns loading and refresh.
 */
import React, { memo } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Chip, LABEL_CLASS } from '../ui';
import StackTd from '../StackTable';
import { fmtTime } from '../session_allocation/allocation';
import { stackClasses } from '../stack_table';
import LeadsSelect from './LeadsSelect';
import RefreshCountdown from './RefreshCountdown';
import {
  DEFAULT_PERIOD,
  LEVEL_TONE,
  PERIODS,
  dayHeading,
  daysToPayCell,
  fmtDay,
  fmtDayTime,
  groupCountLabel,
  groupRowsByIstDate,
  phoneDayHeading,
  helpBullets,
  overdueDelta,
  leadStatusCell,
  lastTouchCell,
  nowWithLabel,
  periodLabel,
  planLabel,
  sectionCount,
  sectionMeta,
  sectionsFor,
  shiftView,
  signalCell,
  refreshDueAt,
  touchBeforeLabel,
  trialStatusCell,
  updatedLabel,
  viaLabel
} from './my_leads';
import type {
  Cell,
  CellTone,
  GroupNoun,
  HeadingVerb,
  SectionId,
  ScoreCell,
  ScoreTable,
  ShiftTile
} from './my_leads';
import type { LeadsPeriod, MyLeadsData } from './types';
import type { RepLevel } from '../../sales_team/api';

const ST = stackClasses('md');
// Same table look as the Advisors and Sales users tabs.
// 20px card gutter on the outer columns, matching the panel header and shift body.
const EDGE = 'md:first:pl-5 md:last:pr-5';
const TH = `px-3.5 py-2 text-left ${EDGE} ${LABEL_CLASS}`;
// Body cells: the rowSpan date cell is first only on a day's first row, so no first-edge pad.
const TD =
  'px-3.5 py-2.5 text-left align-top text-[13px] font-normal text-content-primary md:last:pr-5';
const NUM = 'whitespace-nowrap tabular-nums';
// border-x-0/b-0 undo common.css's all-side tr border (as the Tasks list does).
const ROW = `border-x-0 border-b-0 border-t border-line-subtle text-[12.5px] hover:bg-surface-sunken max-md:border max-md:border-line-subtle ${ST.row}`;
const CARD = 'rounded-[10px] border border-line-subtle bg-surface-card';

const TONE_CLASS: Record<CellTone, string> = {
  plain: '',
  muted: 'text-content-muted',
  success: 'font-semibold text-state-success-text',
  warning: 'font-semibold text-state-warning-text',
  error: 'font-bold text-state-error-text',
  info: 'text-state-info-text'
};

function ToneText({ cell }: { cell: Cell }) {
  return <span className={TONE_CLASS[cell.tone]}>{cell.text}</span>;
}

function StudentLink({ uuid, name }: { uuid: string; name: string }) {
  return (
    <Link
      to={`/studentprofile?id=${encodeURIComponent(uuid)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="break-words font-semibold text-content-link hover:underline"
    >
      {name || 'Unnamed student'}
    </Link>
  );
}

// Card with a tinted title strip, as the Session allocation groups.
export function Panel({
  testId,
  title,
  sub,
  aside,
  asideTestId,
  children
}: {
  testId: string;
  title: string;
  sub?: ReactNode;
  aside?: ReactNode;
  asideTestId?: string;
  children: ReactNode;
}) {
  return (
    <section aria-label={title} data-testid={testId} className={`overflow-hidden ${CARD}`}>
      <div className="flex flex-col gap-0.5 border-b border-line-subtle bg-surface-row-muted px-4 py-3 sm:px-5">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
          <h3 className="m-0 text-[13px] font-bold text-content-primary">{title}</h3>
          {aside ? (
            <span
              data-testid={asideTestId}
              className="text-xs tabular-nums text-content-secondary max-sm:w-full sm:ml-auto"
            >
              {aside}
            </span>
          ) : null}
        </div>
        {sub ? <p className="m-0 text-xs text-content-muted">{sub}</p> : null}
      </div>
      {children}
    </section>
  );
}

interface Col {
  label: string;
  // Share of the table width; table-fixed would otherwise split it evenly.
  width: string;
  num?: boolean;
}

function Table({ cols, children }: { cols: Col[]; children: ReactNode }) {
  // Zero margin/border: the CMS's global table rule would inset it inside the card.
  return (
    <table className={ST.table} style={{ borderCollapse: 'collapse', margin: 0, border: 0 }}>
      <thead
        className={`border-x-0 border-t-0 border-b border-line-subtle bg-surface-sunken ${ST.head}`}
      >
        <tr className="border-0">
          {cols.map((c) => (
            <th
              key={c.label}
              scope="col"
              className={`${TH} ${c.num ? '!text-right' : ''}`}
              style={{ width: c.width }}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className={`${ST.body} max-md:py-1`}>{children}</tbody>
    </table>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="m-0 px-5 py-8 text-center text-[13px] text-content-muted">{text}</p>;
}

// Every row table leads with a Date column: one cell per IST day, spanning that day's rows.
const DATE_COL = (label: string): Col => ({ label, width: '13%' });
const L1_COLS: Col[] = [
  DATE_COL('Trial date'),
  { label: 'Student', width: '19%' },
  { label: 'Booked on', width: '15%' },
  { label: 'Trial (IST)', width: '10%' },
  { label: 'Status', width: '12%' },
  { label: 'Via', width: '13%' },
  { label: 'Now with', width: '18%' }
];
const L2_COLS: Col[] = [
  DATE_COL('Trial date'),
  { label: 'Student', width: '23%' },
  { label: 'Trial (IST)', width: '11%' },
  { label: 'Joined?', width: '14%' },
  { label: 'My touch before', width: '19%' },
  { label: 'Now with', width: '20%' }
];
// Joined / Paid on live in the Date column, so they have no column of their own.
const JOINED_COLS: Col[] = [
  DATE_COL('Joined'),
  { label: 'Student', width: '22%' },
  { label: 'Status', width: '19%' },
  { label: 'Last touch', width: '22%' },
  { label: 'Signal', width: '24%' }
];
// No "Credited to": the section is the rep's own cohort, so it would always read "You".
const CONVERTED_COLS: Col[] = [
  DATE_COL('Paid on'),
  { label: 'Student', width: '28%' },
  { label: 'Joined', width: '15%' },
  { label: 'Days to pay', width: '14%', num: true },
  { label: 'Plan', width: '30%' }
];

// Desktop: a rowSpan date cell on each day's first row. Phone (cards): a heading row per day.
function DateRows<T>({
  id,
  rows,
  getIso,
  noun,
  verb,
  now,
  rowKey,
  cells
}: {
  id: SectionId;
  rows: T[];
  getIso: (r: T) => string | null | undefined;
  noun: GroupNoun;
  verb?: HeadingVerb;
  now: number;
  rowKey: (r: T) => string;
  cells: (r: T) => ReactNode;
}) {
  return (
    <>
      {groupRowsByIstDate(rows, getIso).map((g, gi) => {
        const heading = dayHeading(g.day, now);
        const count = groupCountLabel(g.rows.length, noun);
        const key = `${id}-${g.day ?? 'none'}`;
        return (
          <React.Fragment key={key}>
            <tr
              data-testid="leads-date-heading"
              className={`hidden border-0 ${ST.row} max-md:!my-0`}
            >
              <td className="block px-3 pb-0.5 pt-3 text-left text-[13px] text-content-primary">
                <span className="font-bold">{phoneDayHeading(heading, verb)}</span>
                <span className="text-xs text-content-muted"> · {count}</span>
              </td>
            </tr>
            {g.rows.map((r, ri) => (
              <tr
                key={rowKey(r)}
                className={`${ROW} ${ri === 0 && gi > 0 ? 'md:border-t-line' : ''}`}
              >
                {ri === 0 ? (
                  <td
                    rowSpan={g.rows.length}
                    data-testid="leads-date-cell"
                    className="bg-surface-card px-3.5 py-2.5 text-left align-top max-md:hidden md:border-r md:border-line-subtle md:pl-5"
                  >
                    <span className="block text-[13px] font-bold leading-5 text-content-primary">
                      {heading}
                    </span>
                    <span className="block text-xs tabular-nums text-content-muted">{count}</span>
                  </td>
                ) : null}
                {cells(r)}
              </tr>
            ))}
          </React.Fragment>
        );
      })}
    </>
  );
}

function SectionBody({ id, data, now }: { id: SectionId; data: MyLeadsData; now: number }) {
  const meta = sectionMeta(id);
  const count = sectionCount(data, id);
  const wrap = (rows: number, table: ReactNode) => (
    <Panel
      testId={`leads-section-${id}`}
      title={meta.title}
      sub={meta.sub}
      aside={count}
      asideTestId={`leads-count-${id}`}
    >
      {rows ? table : <Empty text={meta.empty} />}
    </Panel>
  );

  if (id === 'l1_trials') {
    const rows = data.l1?.rows || [];
    return wrap(
      rows.length,
      <Table cols={L1_COLS}>
        <DateRows
          id={id}
          rows={rows}
          getIso={(r) => r.start_at}
          noun="trial"
          now={now}
          rowKey={(r) => `${r.student_uuid}-${r.start_at}`}
          cells={(r) => (
            <>
              <StackTd className={TD}>
                <StudentLink uuid={r.student_uuid} name={r.student_name} />
              </StackTd>
              <StackTd label="Booked on" className={`${TD} ${NUM}`}>
                {fmtDayTime(r.booked_at)}
              </StackTd>
              <StackTd label="Trial (IST)" className={`${TD} ${NUM}`}>
                {fmtTime(r.start_at) || '—'}
              </StackTd>
              <StackTd label="Status" className={TD}>
                <ToneText cell={trialStatusCell(r.status, 'L1')} />
              </StackTd>
              <StackTd label="Via" className={TD}>
                {viaLabel(r.via)}
              </StackTd>
              <StackTd label="Now with" className={`${TD} text-content-secondary`}>
                {nowWithLabel(r.now_with)}
              </StackTd>
            </>
          )}
        />
      </Table>
    );
  }

  if (id === 'l2_trials') {
    const rows = data.l2?.rows || [];
    return wrap(
      rows.length,
      <Table cols={L2_COLS}>
        <DateRows
          id={id}
          rows={rows}
          getIso={(r) => r.start_at}
          noun="trial"
          now={now}
          rowKey={(r) => `${r.student_uuid}-${r.start_at}`}
          cells={(r) => (
            <>
              <StackTd className={TD}>
                <StudentLink uuid={r.student_uuid} name={r.student_name} />
              </StackTd>
              <StackTd label="Trial (IST)" className={`${TD} ${NUM}`}>
                {fmtTime(r.start_at) || '—'}
              </StackTd>
              <StackTd label="Joined?" className={TD}>
                <ToneText cell={trialStatusCell(r.status, 'L2')} />
              </StackTd>
              <StackTd label="My touch before" className={TD}>
                <ToneText cell={touchBeforeLabel(r.my_touch_before, r.start_at)} />
              </StackTd>
              <StackTd label="Now with" className={`${TD} text-content-secondary`}>
                {nowWithLabel(r.now_with)}
              </StackTd>
            </>
          )}
        />
      </Table>
    );
  }

  if (id === 'leads_joined') {
    const rows = data.leads_joined?.rows || [];
    return wrap(
      rows.length,
      <Table cols={JOINED_COLS}>
        <DateRows
          id={id}
          rows={rows}
          getIso={(r) => r.joined_at}
          noun="lead"
          verb="joined"
          now={now}
          rowKey={(r) => `${r.student_uuid}-${r.joined_at}`}
          cells={(r) => (
            <>
              <StackTd className={TD}>
                <StudentLink uuid={r.student_uuid} name={r.student_name} />
              </StackTd>
              <StackTd label="Status" className={TD}>
                <ToneText cell={leadStatusCell(r)} />
              </StackTd>
              <StackTd label="Last touch" className={TD}>
                <ToneText cell={lastTouchCell(r.last_touch, r.joined_at, now)} />
              </StackTd>
              <StackTd label="Signal" className={TD}>
                <ToneText cell={signalCell(r.signal, now)} />
              </StackTd>
            </>
          )}
        />
      </Table>
    );
  }

  const rows = data.converted?.rows || [];
  return wrap(
    rows.length,
    <Table cols={CONVERTED_COLS}>
      <DateRows
        id={id}
        rows={rows}
        getIso={(r) => r.paid_at}
        noun="member"
        verb="paid"
        now={now}
        rowKey={(r) => `${r.student_uuid}-${r.paid_at}`}
        cells={(r) => (
          <>
            <StackTd className={TD}>
              <StudentLink uuid={r.student_uuid} name={r.student_name} />
            </StackTd>
            <StackTd label="Joined" className={`${TD} ${NUM}`}>
              {r.joined_at ? fmtDay(r.joined_at) : <span className="text-content-muted">—</span>}
            </StackTd>
            <StackTd label="Days to pay" className={`${TD} ${NUM} md:text-right`}>
              <ToneText cell={daysToPayCell(r.days_to_pay)} />
            </StackTd>
            <StackTd label="Plan" className={TD}>
              {planLabel(r.plan_weeks, r.plan_label)}
            </StackTd>
          </>
        )}
      />
    </Table>
  );
}

// One KPI tile look for the page tiles.
const STAT_LABEL = 'text-xs text-content-muted';
const STAT_VALUE = 'text-2xl font-semibold leading-8 tabular-nums text-content-primary';
const STAT_SUB = 'text-xs text-content-muted';

const TILE_TONE: Record<ShiftTile['tone'], { box: string; value: string }> = {
  error: { box: 'bg-state-error-bg', value: 'text-state-error-text' },
  info: { box: 'bg-state-info-bg', value: 'text-state-info-text' },
  success: { box: 'bg-state-success-bg', value: 'text-state-success-text' },
  warning: { box: 'bg-state-warning-bg', value: 'text-state-warning-text' },
  neutral: { box: 'border border-line-subtle', value: 'text-content-primary' }
};

const DELTA_TONE = { success: 'text-state-success-text', error: 'text-state-error-text', '': '' };

export function OverdueChange({ delta }: { delta: number | null }) {
  const d = overdueDelta(delta);
  if (!d.text) return null;
  return (
    <span
      className={`ml-1.5 text-[11px] font-semibold ${DELTA_TONE[d.tone]}`}
      title={
        d.tone === 'success'
          ? 'Fewer overdue than the hour before'
          : 'More overdue than the hour before'
      }
    >
      {d.text}
    </span>
  );
}

const BADGE_TONE = {
  success: 'bg-state-success-bg text-state-success-text',
  error: 'bg-state-error-bg text-state-error-text'
};

function CellText({ c }: { c: ScoreCell }) {
  return (
    <>
      <span className={c.bad ? 'font-semibold text-state-error-text' : ''}>{c.text}</span>
      {c.waiting ? (
        <span className="ml-1 text-[11px] text-content-muted max-sm:ml-0 max-sm:block">
          {c.waiting}
        </span>
      ) : null}
    </>
  );
}

// Full width, stacked one after another; columns fit a phone, so the page never scrolls sideways.
function ScoreTableView({ t }: { t: ScoreTable }) {
  const th = `px-3.5 py-2 text-right align-bottom first:text-left max-sm:px-1.5 ${LABEL_CLASS}`;
  const td = 'px-3.5 py-2 text-right tabular-nums first:text-left max-sm:px-1.5';
  return (
    <div className="flex flex-col gap-2" data-testid={`score-table-${t.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold leading-5 text-content-secondary">{t.title}</span>
        <span
          data-testid={`score-badge-${t.key}`}
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${BADGE_TONE[t.badge.tone]}`}
        >
          {t.badge.text}
        </span>
      </div>
      <div className="overflow-hidden rounded-[10px] border border-line-subtle">
        <table
          className="w-full text-[12.5px] max-sm:text-[12px]"
          style={{ borderCollapse: 'collapse', margin: 0, border: 0 }}
        >
          <thead>
            <tr className="border-0 bg-surface-sunken">
              <th className={th}>
                Hour<span className="max-sm:hidden"> (IST)</span>
              </th>
              {t.columns.map((c) => (
                <th key={c.label} className={th}>
                  <span className="max-sm:hidden">{c.label}</span>
                  <span className="sm:hidden">{c.short}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {t.rows.map((r) => (
              <tr
                key={r.label}
                data-testid={`score-row-${t.key}-${r.label}`}
                className={`border-x-0 border-b-0 border-t border-line-subtle ${
                  r.current ? 'bg-state-info-bg' : ''
                } ${r.future ? 'text-content-muted' : 'text-content-primary'}`}
              >
                <td className={td}>
                  <span className={`whitespace-nowrap ${r.current ? 'font-semibold' : ''}`}>
                    {/* Phones show the start only, so each hour stays on one line. */}
                    {r.label.split('\u2013')[0]}
                    {r.label.includes('\u2013') ? (
                      <span className="max-sm:hidden">{`\u2013${r.label.split('\u2013')[1]}`}</span>
                    ) : null}
                  </span>
                  {r.current ? (
                    <span className="ml-1.5 text-[11px] text-content-muted max-sm:ml-0 max-sm:block">
                      now
                    </span>
                  ) : null}
                </td>
                {r.cells.map((c, i) => (
                  <td key={i} className={td}>
                    <CellText c={c} />
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-x-0 border-b-0 border-t border-line-subtle bg-surface-sunken font-semibold text-content-primary">
              <td className={td}>Today</td>
              {t.total.map((c, i) => (
                <td key={i} className={td}>
                  <CellText c={c} />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ShiftCard({ data, now, viewing }: { data: MyLeadsData; now: number; viewing?: boolean }) {
  const v = shiftView(
    data.shift,
    data.scorecard,
    data.rep.level as RepLevel,
    now,
    viewing ? data.rep.name : undefined
  );
  return (
    <Panel
      testId="leads-shift"
      title={viewing ? 'Shift today' : 'My shift today'}
      sub={v.sub || undefined}
      aside={
        <>
          Work these from the <b className="font-semibold text-content-primary">Tasks</b> tab
        </>
      }
    >
      {v.mode === 'message' ? (
        <Empty text={v.message} />
      ) : (
        <div className="flex flex-col gap-4 px-4 py-5 sm:px-5 max-sm:px-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {v.tiles.map((t) => (
              <div
                key={t.key}
                data-testid={`shift-tile-${t.key}`}
                className={`flex min-w-0 flex-col gap-0.5 rounded-[10px] px-4 py-3 ${TILE_TONE[t.tone].box}`}
              >
                <span className="text-xs font-semibold text-content-primary">{t.label}</span>
                <span
                  className={`text-[28px] font-semibold leading-9 tabular-nums ${TILE_TONE[t.tone].value}`}
                >
                  {t.value}
                </span>
                <span className="text-xs text-content-secondary">{t.detail}</span>
              </div>
            ))}
          </div>
          {v.tables.map((t) => (
            <ScoreTableView key={t.key} t={t} />
          ))}
          {v.footer ? (
            <p data-testid="shift-footer" className="m-0 text-xs text-content-muted">
              {v.footer}
            </p>
          ) : null}
        </div>
      )}
    </Panel>
  );
}

interface ViewProps {
  data: MyLeadsData;
  // Reference time for "ago" labels and the current shift hour; fixed per response.
  now: number;
  period: LeadsPeriod;
  onPeriod: (p: LeadsPeriod) => void;
  // Team's Leads view: a manager looking at someone else's page.
  viewing?: boolean;
  loading?: boolean;
  // The shown numbers are for another period while the new one loads.
  stale?: boolean;
  banner?: ReactNode;
  // Team's Leads rep dropdown, shown in place of the page title.
  picker?: ReactNode;
}

// Team's Leads card holding only the rep dropdown: nobody picked yet, or the picked page has no view.
export function PickerBar({ children }: { children: ReactNode }) {
  return (
    <section
      aria-label="Pick a team member"
      data-testid="team-leads-toolbar"
      className={`mx-[5%] mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 px-3.5 py-3 max-sm:mx-3 max-sm:flex-col max-sm:items-stretch ${CARD}`}
    >
      {children}
    </section>
  );
}

function MyLeadsView({
  data,
  now,
  period,
  onPeriod,
  viewing,
  loading,
  stale,
  banner,
  picker
}: ViewProps) {
  const level = data.rep.level as RepLevel;
  const updated = updatedLabel(data.generated_at);
  const dueAt = refreshDueAt(data.generated_at, data.next_refresh_at, now, data.server_time);
  const chip = (
    <Chip tone={LEVEL_TONE[level]} testId="leads-level-chip">
      {level} view
    </Chip>
  );
  return (
    <div
      className="mx-[5%] my-5 flex flex-col gap-3.5 text-[13px] text-content-primary max-sm:mx-3"
      data-testid="my-leads-view"
    >
      {/* Summary bar, as on Session allocation: title left, controls and refresh state right. */}
      <section
        aria-label="Leads summary"
        data-testid="leads-header"
        className={`flex flex-wrap items-center gap-x-4 gap-y-2.5 px-3.5 py-3 max-sm:flex-col max-sm:items-stretch ${CARD}`}
      >
        <div className={`flex min-w-0 flex-col ${picker ? 'gap-1.5' : 'gap-0.5'}`}>
          {picker ? (
            // Team's Leads: the rep dropdown stands in for the title; it already shows the name.
            <div className="flex min-w-0 items-center gap-2">
              {picker}
              <span className="shrink-0">{chip}</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="m-0 text-[15px] font-bold leading-5 text-content-primary">
                {viewing ? `${data.rep.name}'s leads` : 'My Leads'}
              </h2>
              {chip}
            </div>
          )}
          <span className="text-xs text-content-secondary">
            {viewing
              ? `The same page ${data.rep.name} sees as My Leads.`
              : 'Only your own students. Your manager sees the same page for any rep.'}
          </span>
        </div>
        <span className="flex-1 max-sm:hidden" />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 max-sm:flex-col max-sm:items-stretch">
          <LeadsSelect
            id="my-leads-period"
            label="Period"
            value={String(period)}
            options={PERIODS.map((p) => ({ value: String(p), label: periodLabel(p) }))}
            onChange={(v) => onPeriod(Number(v) as LeadsPeriod)}
            active={period !== DEFAULT_PERIOD}
          />
          <span
            className="flex flex-wrap gap-x-1.5 text-[11.5px] tabular-nums text-content-muted sm:flex-col"
            aria-live="off"
            data-testid="leads-refresh"
          >
            <RefreshCountdown dueAt={dueAt} loading={loading} />
            {updated ? (
              <span>
                <span aria-hidden="true" className="sm:hidden">
                  ·{' '}
                </span>
                {updated}
              </span>
            ) : null}
          </span>
        </div>
      </section>

      {banner}

      <div
        className="flex flex-col gap-3.5 transition-opacity"
        aria-busy={stale || undefined}
        style={stale ? { opacity: 0.5 } : undefined}
        data-testid="leads-body"
      >
        <ShiftCard data={data} now={now} viewing={viewing} />

        {sectionsFor(level).map((id) => (
          <SectionBody key={id} id={id} data={data} now={now} />
        ))}
      </div>

      <Panel testId="leads-help" title="How these numbers work">
        <ul className="m-0 list-disc py-4 pl-9 pr-5 text-xs leading-[1.5] text-content-secondary">
          {helpBullets(level, data.period_start_ist, data.period_end_ist).map((b) => (
            <li key={b} className="py-0.5">
              {b}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

// Memoised so the countdown tick and unrelated page state do not re-render the tables.
const MemoMyLeadsView = memo(MyLeadsView);

export default MemoMyLeadsView;
